"use client";

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/navigation';
import {
  Mic, MicOff, X, Send, Bot, User
} from 'lucide-react';
import { processAICommand, processAudioCommand, AIOrchestratorResponse } from '@/lib/gemini';
import { useAuth } from '@/contexts/AuthContext';

interface ConversationMessage {
  role: 'assistant' | 'user';
  text: string;
  timestamp: Date;
}

interface VoiceAssistantProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onTenderGenerated?: (data: any) => void;
  isOpen: boolean;
  onClose: () => void;
}

export default function VoiceAssistant({ onTenderGenerated, isOpen, onClose }: VoiceAssistantProps) {
  const { t, i18n } = useTranslation('common');
  const router = useRouter();
  const { activeRole } = useAuth();
  const lang = (i18n.language || 'he') as 'ru' | 'en' | 'he' | 'fr';

  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [isListening, setIsListening] = useState(false);
  const micConfig = useMemo(() => {
    if (typeof window === 'undefined') return { mode: 'none' as const, supported: false };
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    // ReactNativeWebView is injected by the Expo app shell on both iOS & Android.
    // On iOS WKWebView the Web Speech API object exists but doesn't actually
    // work — Apple ships it only in Safari. So in the iOS native shell we must
    // skip SpeechRecognition and use MediaRecorder + Gemini transcription.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isNativeWebView = !!(window as any).ReactNativeWebView;
    const speechApiActuallyWorks =
      ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) &&
      !(isIOS && isNativeWebView);
    const hasMediaDevices = !!navigator.mediaDevices?.getUserMedia;
    if (speechApiActuallyWorks) return { mode: 'speech-api' as const, supported: true };
    if (hasMediaDevices) return { mode: 'media-recorder' as const, supported: true };
    return { mode: 'none' as const, supported: false };
  }, []);
  const micSupported = micConfig.supported;
  const micMode = micConfig.mode;
  const [textInput, setTextInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recogRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const _voiceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // micConfig is computed via useMemo above

  const greetedRef = useRef(false);
  useEffect(() => {
    if (isOpen && !greetedRef.current && messages.length === 0) {
      greetedRef.current = true;
      const greetings: Record<string, string> = {
        ru: 'Чем могу помочь?',
        en: 'How can I help?',
        he: 'במה אפשר לעזור?',
        fr: 'Comment puis-je vous aider?'
      };
      // Defer to avoid synchronous setState in effect
      queueMicrotask(() => {
        setMessages([{ role: 'assistant', text: greetings[lang] || greetings.en, timestamp: new Date() }]);
      });
    }
    if (!isOpen) greetedRef.current = false;
  }, [isOpen, messages.length, lang]);

  const addAssistantMessage = useCallback((text: string) => {
    setMessages(prev => [...prev, { role: 'assistant', text, timestamp: new Date() }]);
    // Speak using browser SpeechSynthesis
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel(); // Cancel currently speaking phrases
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang === 'he' ? 'he-IL' : lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-US';
      window.speechSynthesis.speak(utterance);
    }
  }, [lang]);

  const handleAIResponse = useCallback((res: AIOrchestratorResponse | null) => {
    if (!res) {
      addAssistantMessage('Извините, произошла ошибка подключения к ИИ. Убедитесь, что настроен API ключ Gemini в переменной NEXT_PUBLIC_GEMINI_API_KEY.');
      return;
    }
    
    addAssistantMessage(res.voice_reply);

    if (res.ui_action === 'search_masters') {
      const q = new URLSearchParams();
      if (res.parameters?.service) q.set('service', res.parameters.service);
      if (res.parameters?.location && res.parameters.location !== '__nearby__') q.set('city', res.parameters.location);
      
      setTimeout(() => {
        router.push(`/catalog?${q.toString()}`);
        onClose();
      }, 2000);
    } else if (res.ui_action === 'create_tender' && onTenderGenerated) {
      setTimeout(() => {
        onTenderGenerated(res.parameters);
        onClose();
      }, 2000);
    }
    // other ui_actions can be handled here (book_appointment, cancel_day, create_promo)
  }, [addAssistantMessage, router, onClose, onTenderGenerated]);

  const processUserInput = useCallback(async (input: string) => {
    setMessages(prev => [...prev, { role: 'user', text: input, timestamp: new Date() }]);
    setIsThinking(true);

    const context = { role: activeRole || 'client', lang };
    
    // Pass recent context as history, plus current transcript
    const historyText = messages.slice(-5).map(m => `${m.role}: ${m.text}`).join('\n');
    const fullTranscript = historyText ? `${historyText}\nUser: ${input}` : input;

    const res = await processAICommand(fullTranscript, context);
    setIsThinking(false);
    
    if (res) {
      handleAIResponse(res);
    } else {
      addAssistantMessage(t('voice.error', 'Произошла ошибка связи с ИИ. Попробуйте еще раз.'));
    }
  }, [lang, activeRole, messages, addAssistantMessage, t, handleAIResponse]);

  // --- Speech API method (Chrome, Safari, desktop browsers) ---
  const startListeningSpeechAPI = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.lang = lang === 'he' ? 'he-IL' : lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      recognition.stop();
      if (_voiceTimeout.current) clearTimeout(_voiceTimeout.current);
      _voiceTimeout.current = setTimeout(() => {
        if (transcript) processUserInput(transcript);
      }, 300);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onerror = (event: any) => {
      console.error('SpeechRecognition error:', event?.error);
      setIsListening(false);
      addAssistantMessage(`${t('voice.mic_error', 'שגיאת מיקרופון')}: ${event?.error || ''}`);
    };
    recognition.onend = () => setIsListening(false);

    recogRef.current = recognition;
    recognition.start();
  };

  // --- MediaRecorder method (WebView / iOS WKWebView fallback) ---
  const startListeningMediaRecorder = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/mp4'
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks to release mic
        stream.getTracks().forEach(t => t.stop());

        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        
        if (audioBlob.size < 1000) {
          // Too short, probably no speech
          setIsListening(false);
          return;
        }

        // Show user that we are processing their voice
        setMessages(prev => [...prev, { role: 'user', text: '🎙️ ...', timestamp: new Date() }]);
        setIsThinking(true);
        setIsListening(false);

        const context = { role: activeRole || 'client', lang };
        const res = await processAudioCommand(audioBlob, context);
        setIsThinking(false);

        // Replace the placeholder "🎙️ ..." with extracted transcript if available
        if (res) {
          // Remove placeholder message
          setMessages(prev => {
            const updated = [...prev];
            const lastUserIdx = updated.findLastIndex(m => m.role === 'user' && m.text === '🎙️ ...');
            if (lastUserIdx >= 0) {
              updated.splice(lastUserIdx, 1);
            }
            return updated;
          });
          handleAIResponse(res);
        } else {
          setMessages(prev => {
            const updated = [...prev];
            const lastUserIdx = updated.findLastIndex(m => m.role === 'user' && m.text === '🎙️ ...');
            if (lastUserIdx >= 0) {
              updated.splice(lastUserIdx, 1);
            }
            return updated;
          });
          addAssistantMessage(t('voice.error', 'Произошла ошибка связи с ИИ. Попробуйте еще раз.'));
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsListening(true);
    } catch (err) {
      console.error('MediaRecorder error:', err);
      setIsListening(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      addAssistantMessage(`${t('voice.mic_error', 'שגיאת מיקרופון')}: ${(err as any)?.name || String(err)}`);
    }
  };

  const startListening = () => {
    if (micMode === 'speech-api') {
      startListeningSpeechAPI();
    } else if (micMode === 'media-recorder') {
      startListeningMediaRecorder();
    }
  };

  const stopListening = () => {
    if (micMode === 'speech-api' && recogRef.current) {
      recogRef.current.stop();
    } else if (micMode === 'media-recorder' && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    setIsListening(false);
  };

  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!textInput.trim() || isThinking) return;
    const val = textInput.trim();
    setTextInput('');
    processUserInput(val);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col sm:p-4 bg-background/80 backdrop-blur-sm sm:items-center sm:justify-center">
      <div className="flex-1 w-full sm:max-w-md bg-card sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden sm:h-[80vh] sm:max-h-[800px] border border-border animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-border bg-card/50 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-linear-to-tr from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20">
              <Bot className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="font-black text-lg leading-tight">YofiBot ✨</h3>
              <p className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                AI-Оркестратор
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-secondary text-muted-foreground flex items-center justify-center hover:bg-secondary/80 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex gap-2.5 max-w-[85%] ${msg.role === 'user' ? 'ml-auto flex-row-reverse' : ''}`}>
              <div className={`shrink-0 w-7 h-7 rounded-xl flex items-center justify-center ${msg.role === 'user' ? 'bg-secondary' : 'bg-linear-to-br from-primary/20 to-primary/5'}`}>
                {msg.role === 'user' ? <User className="w-3.5 h-3.5 text-foreground/60" /> : <Bot className="w-3.5 h-3.5 text-primary" />}
              </div>
              <div className={`px-4 py-3 text-[15px] leading-relaxed shadow-sm ${msg.role === 'user' ? 'bg-primary text-primary-foreground rounded-2xl rounded-tr-md' : 'bg-secondary/80 text-foreground rounded-2xl rounded-tl-md'}`}>
                {msg.text}
              </div>
            </div>
          ))}

          {isThinking && (
            <div className="flex gap-2.5">
              <div className="shrink-0 w-7 h-7 rounded-xl bg-linear-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <Bot className="w-3.5 h-3.5 text-primary" />
              </div>
              <div className="bg-secondary/80 px-4 py-3 rounded-2xl rounded-tl-md">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
                  <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
                  <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Bar */}
        <div className="shrink-0 border-t border-border bg-card px-4 py-3">
          <form onSubmit={handleTextSubmit} className="flex gap-2 items-center">
            <input
              type="text"
              value={textInput}
              onChange={e => setTextInput(e.target.value)}
              placeholder={t('voice.placeholder', 'Напишите или продиктуйте...')}
              className="flex-1 bg-secondary border border-border rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
              disabled={isThinking}
            />

            {micSupported && (
              <button
                type="button"
                onClick={isListening ? stopListening : startListening}
                className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                  isListening
                    ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/30'
                    : 'bg-primary/10 text-primary hover:bg-primary/20'
                }`}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}

            <button
              type="submit"
              disabled={!textInput.trim() || isThinking}
              className="shrink-0 w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          
          {!micSupported && (
            <p className="mt-2 text-center text-[10px] text-muted-foreground opacity-70">
              💡 {t('voice.keyboard_hint', 'Используйте значок микрофона 🎤 на вашей клавиатуре для голосового ввода')}
            </p>
          )}
        </div>

      </div>
    </div>
  );
}
